import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextareaAutosize,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Share as ShareIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Autorenew as ProcessingIcon,
  Cancel as CancelIcon,
  BarChart as BarChartIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { isLocalInternalHost } from "../../utils/localInternalHost";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api";

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "due", label: "Due" },
  { value: "pending", label: "Pending" },
];

const QUICK_RANGES = [
  { id: "today", label: "Today" },
  { id: "last7", label: "Last 7 Days" },
  { id: "last30", label: "Last 30 Days" },
  { id: "thisMonth", label: "This Month" },
  { id: "lastMonth", label: "Last Month" },
  { id: "thisYear", label: "This Year" },
  { id: "custom", label: "Custom" },
];

const WORK_STATUS_OPTIONS = [
  {
    value: "completed",
    label: "Completed",
    description: "Work finished & delivered to customer",
    icon: CheckCircleIcon,
    color: "success",
  },
  {
    value: "pending",
    label: "Pending",
    description: "Waiting for customer documents or payment",
    icon: HourglassIcon,
    color: "warning",
  },
  {
    value: "under_processing",
    label: "In Progress",
    description: "Processing on portals / department follow-up",
    icon: ProcessingIcon,
    color: "info",
  },
  {
    value: "cancelled",
    label: "Cancelled",
    description: "Stopped based on request or duplication",
    icon: CancelIcon,
    color: "error",
  },
];

const DEFAULT_STATUS_REMARK = {
  completed: "Work delivered successfully.",
  pending: "Waiting for customer response/documents.",
  under_processing: "Processing with department/portal.",
  cancelled: "Cancelled based on customer instruction.",
};

const OldSiteBills = () => {
  const { hasRole } = useAuth();
  const isOperator = hasRole("app_operator");
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [quickRange, setQuickRange] = useState("thisYear");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [workStatusDialogOpen, setWorkStatusDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [workStatus, setWorkStatus] = useState("completed");
  const [workRemark, setWorkRemark] = useState("");
  const [updatingWorkStatus, setUpdatingWorkStatus] = useState(false);
  const [yearwiseStats, setYearwiseStats] = useState([]);
  const [yearwiseLoading, setYearwiseLoading] = useState(false);

  useEffect(() => {
    // Clear custom dates when quick range changes
    if (quickRange !== "custom") {
      setFromDate("");
      setToDate("");
    }
  }, [quickRange]);

  useEffect(() => {
    // Fetch year-wise stats on component mount - only if not operator
    if (!isOperator) {
      fetchYearwiseStats();
    }
  }, [isOperator]);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const fetchBillsWithFilters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("admin_token");

      // Build query parameters for server-side filtering
      const params = {
        page: 1,
        limit: 200, // Increased to get more results
      };

      // Add search parameter if searching (use debounced search term)
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
        if (searchType !== "all") {
          params.searchType = searchType;
        }
      }

      // Add status filter
      if (statusFilter !== "all") {
        params.paymentStatus = statusFilter;
      }

      // Add date range parameters
      if (quickRange === "custom") {
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
      } else if (quickRange !== "thisYear") {
        params.dateRange = quickRange;
      }

      const response = await axios.get(`${API_BASE_URL}/bills`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data.success && response.data.data) {
        const billList = response.data.data || [];
        setBills(billList);
        setFilteredBills(billList);
        setError(null);

        console.log(
          `✅ Loaded ${billList.length} bills (Total: ${response.data.pagination?.total || "?"})`,
        );
      } else {
        setBills([]);
        setFilteredBills([]);
        setError(response.data?.message || "Failed to load bills");
      }
    } catch (error) {
      console.error("Error fetching bills:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load bills";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearchTerm,
    statusFilter,
    quickRange,
    fromDate,
    toDate,
    searchType,
  ]);

  // Debounce search term - wait 500ms before applying search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Trigger fetch whenever search/filter parameters change
    fetchBillsWithFilters();
  }, [fetchBillsWithFilters]);

  const fetchYearwiseStats = async () => {
    setYearwiseLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API_BASE_URL}/bills/stats/yearwise`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.data) {
        setYearwiseStats(response.data.data || []);
      } else {
        setYearwiseStats([]);
      }
    } catch (error) {
      console.error("Error fetching year-wise stats:", error);
      setYearwiseStats([]);
    } finally {
      setYearwiseLoading(false);
    }
  };

  const sendHiViaWhatsApp = (bill) => {
    if (!bill.customer_phone) {
      toast.error("Customer phone number is required");
      return;
    }

    const phone = bill.customer_phone.replace(/\D/g, "").substring(0, 10);
    if (phone.length !== 10) {
      toast.error("Invalid phone number");
      return;
    }

    const message = "hi";
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const shareViaWhatsApp = (bill) => {
    if (!bill.customer_phone) {
      toast.error("Customer phone number is required");
      return;
    }

    const phone = bill.customer_phone.replace(/\D/g, "").substring(0, 10);
    if (phone.length !== 10) {
      toast.error("Invalid phone number");
      return;
    }

    // Handle items - could be string (comma-separated) or array
    let itemsList = "N/A";
    if (bill.items) {
      if (typeof bill.items === "string") {
        // Comma-separated services from bill_generator
        const services = bill.items
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s);
        if (services.length > 0) {
          itemsList = services.map((s) => `• ${s}`).join("\n");
        }
      } else if (Array.isArray(bill.items)) {
        itemsList = bill.items
          .map((item) => {
            const name = item.service_name || item.name || "Service";
            const qty = item.quantity || 1;
            const total = item.total || item.price || 0;
            return `• ${name} - Qty: ${qty} - ₹${Number(total).toFixed(2)}`;
          })
          .join("\n");
      }
    }

    const workStatusValue = bill.work_status || "completed";
    const workStatusLabel = getWorkStatusLabel(workStatusValue);
    const workEmoji =
      workStatusValue === "completed"
        ? "✅"
        : workStatusValue === "pending"
          ? "⏳"
          : workStatusValue === "under_processing"
            ? "⚙️"
            : "❌";
    const paymentStatus = (bill.payment_status || "pending").toUpperCase();
    const paymentEmoji =
      bill.payment_status === "paid"
        ? "✅"
        : bill.payment_status === "partial"
          ? "⚠️"
          : "⏳";
    const billDate = bill.bill_date || bill.created_at || "N/A";
    const paidAmount =
      bill.paid_amount >= 0
        ? bill.paid_amount
        : (bill.amount || 0) - (bill.due_amount || 0);

    const message =
      `📋 *Bill Details - ${bill.bill_number || bill.id}*\n\n` +
      `👤 *Customer:* ${bill.customer_name || "N/A"}\n` +
      `📞 *Phone:* ${bill.customer_phone}\n` +
      `📅 *Date:* ${billDate}\n\n` +
      `💰 *Amount Details:*\n` +
      `*Total: ₹${Number(bill.amount || 0).toFixed(2)}*\n` +
      `Paid: ₹${Number(paidAmount).toFixed(2)}\n` +
      `Due: ₹${Number(bill.due_amount || 0).toFixed(2)}\n\n` +
      `📦 *Services:*\n${itemsList}\n\n` +
      `${paymentEmoji} *Payment Status:* ${paymentStatus}\n` +
      `${workEmoji} *Work Status:* ${workStatusLabel}\n` +
      `${bill.work_remark ? `📝 *Work Notes:* ${bill.work_remark}\n` : ""}` +
      `${bill.notes ? `📝 *Remarks:* ${bill.notes}\n` : ""}` +
      `\n🙏 Thank you for your business!`;

    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";

      // Format in IST timezone
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "N/A";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";

      return date.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const handleOpenWorkStatusDialog = (bill) => {
    setSelectedBill(bill);
    setWorkStatus(bill.work_status || "completed");
    setWorkRemark(bill.work_remark || "");
    setWorkStatusDialogOpen(true);
  };

  const handleCloseWorkStatusDialog = () => {
    setWorkStatusDialogOpen(false);
    setSelectedBill(null);
    setWorkStatus("completed");
    setWorkRemark("");
  };

  const handleWorkStatusChange = (newStatus) => {
    setWorkStatus(newStatus);
    if (!workRemark || workRemark === DEFAULT_STATUS_REMARK[workStatus]) {
      setWorkRemark(DEFAULT_STATUS_REMARK[newStatus] || "");
    }
  };

  const handleUpdateWorkStatus = async () => {
    if (!selectedBill) return;

    setUpdatingWorkStatus(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.patch(
        `${API_BASE_URL}/bills/${selectedBill.id}/work-status`,
        {
          work_status: workStatus,
          work_remark: workRemark || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        toast.success("Work status updated successfully");

        // Update the bill in the local state
        setBills((prevBills) =>
          prevBills.map((bill) =>
            bill.id === selectedBill.id
              ? { ...bill, work_status: workStatus, work_remark: workRemark }
              : bill,
          ),
        );
        setFilteredBills((prevBills) =>
          prevBills.map((bill) =>
            bill.id === selectedBill.id
              ? { ...bill, work_status: workStatus, work_remark: workRemark }
              : bill,
          ),
        );

        handleCloseWorkStatusDialog();
      } else {
        throw new Error(
          response.data.message || "Failed to update work status",
        );
      }
    } catch (error) {
      console.error("Error updating work status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update work status",
      );
    } finally {
      setUpdatingWorkStatus(false);
    }
  };

  const getWorkStatusColor = (status) => {
    const option = WORK_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.color || "default";
  };

  const getWorkStatusLabel = (status) => {
    const option = WORK_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.label || status;
  };

  if (!isLocalInternalHost()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <HistoryIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography variant="h4" component="h1">
          Old Site Bills
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Year-wise Sales Charts - Hidden for operators */}
      {!isOperator && yearwiseStats && yearwiseStats.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <BarChartIcon color="primary" />
              <Typography variant="h5" component="h2" fontWeight="bold">
                Year-wise Sales Report
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Turnover by Year (₹)
                </Typography>
                {yearwiseLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={yearwiseStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="year"
                        label={{
                          value: "Year",
                          position: "insideBottom",
                          offset: -5,
                        }}
                      />
                      <YAxis
                        label={{
                          value: "Revenue (₹)",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        tickFormatter={(value) => {
                          if (value >= 100000)
                            return `${(value / 100000).toFixed(1)}L`;
                          if (value >= 1000)
                            return `${(value / 1000).toFixed(1)}K`;
                          return value.toString();
                        }}
                      />
                      <Tooltip
                        formatter={(value) =>
                          `₹${new Intl.NumberFormat("en-IN").format(value.toFixed(2))}`
                        }
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Legend />
                      <Bar
                        dataKey="totalRevenue"
                        fill="#8884d8"
                        name="Total Turnover"
                      />
                      <Bar
                        dataKey="paidAmount"
                        fill="#82ca9d"
                        name="Paid Amount"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Bills Count by Year
                </Typography>
                {yearwiseLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={yearwiseStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="year"
                        label={{
                          value: "Year",
                          position: "insideBottom",
                          offset: -5,
                        }}
                      />
                      <YAxis
                        label={{
                          value: "Number of Bills",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip
                        formatter={(value) => value.toLocaleString("en-IN")}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="totalBills"
                        stroke="#8884d8"
                        strokeWidth={2}
                        name="Total Bills"
                        dot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="pendingBills"
                        stroke="#ff7300"
                        strokeWidth={2}
                        name="Pending Bills"
                        dot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom color="text.secondary">
                Year-wise Summary Table
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Year</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total Bills</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total Turnover (₹)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Paid Amount (₹)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Due Amount (₹)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Pending Bills</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {yearwiseStats.map((yearData) => (
                      <TableRow key={yearData.year} hover>
                        <TableCell>
                          <strong>{yearData.year}</strong>
                        </TableCell>
                        <TableCell align="right">
                          {yearData.totalBills.toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell align="right">
                          ₹
                          {yearData.totalRevenue.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell align="right">
                          ₹
                          {yearData.paidAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell align="right">
                          ₹
                          {yearData.dueAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell align="right">
                          {yearData.pendingBills.toLocaleString("en-IN")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Date Range Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Date Range
            </Typography>
            <Chip
              label={
                quickRange === "custom"
                  ? "Custom"
                  : QUICK_RANGES.find((r) => r.id === quickRange)?.label ||
                    "All"
              }
              color="primary"
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {QUICK_RANGES.map((range) => (
              <Button
                key={range.id}
                variant={quickRange === range.id ? "contained" : "outlined"}
                size="small"
                onClick={() => setQuickRange(range.id)}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: quickRange === range.id ? "bold" : "normal",
                }}
              >
                {range.label}
              </Button>
            ))}
          </Box>
          {quickRange === "custom" && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
              }}
            >
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 180 }}
              />
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 180 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                sx={{ alignSelf: "flex-end" }}
              >
                Clear
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by name, phone, service name, or bill number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
            >
              <Typography variant="body2" fontWeight="bold">
                Search Type:
              </Typography>
              <ToggleButtonGroup
                value={searchType}
                exclusive
                onChange={(e, newValue) => newValue && setSearchType(newValue)}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="name">Name</ToggleButton>
                <ToggleButton value="phone">Phone</ToggleButton>
                <ToggleButton value="service">Service</ToggleButton>
                <ToggleButton value="amount">Bill Amount</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={statusFilter}
                label="Payment Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Found {filteredBills.length} bill
            {filteredBills.length !== 1 ? "s" : ""}
            {statusFilter !== "all" &&
              ` (Status: ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label})`}
            {quickRange !== "thisYear" &&
              ` (Date: ${QUICK_RANGES.find((r) => r.id === quickRange)?.label || "Custom"})`}
          </Typography>
        </CardContent>
      </Card>

      {/* Bills List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredBills.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? "No bills match your search" : "No bills found"}
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {filteredBills.map((bill) => {
            const items = bill.items || [];
            const itemsList = Array.isArray(items) ? items : [];

            return (
              <Card key={bill.id} sx={{ "&:hover": { boxShadow: 4 } }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor: "primary.light",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "primary.main",
                          fontWeight: "bold",
                          fontSize: 20,
                        }}
                      >
                        {bill.customer_name?.charAt(0)?.toUpperCase() || "#"}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {bill.customer_name || "Unknown Customer"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mt: 0.5,
                          }}
                        >
                          {bill.customer_phone && (
                            <Typography variant="body2" color="text.secondary">
                              📞 {bill.customer_phone}
                            </Typography>
                          )}
                          {bill.bill_number && (
                            <Typography variant="body2" color="text.secondary">
                              🧾 {bill.bill_number}
                            </Typography>
                          )}
                          {bill.bill_date && (
                            <Typography variant="body2" color="text.secondary">
                              📅 {formatDate(bill.bill_date)}
                            </Typography>
                          )}
                          {bill.created_at && (
                            <Typography variant="body2" color="text.secondary">
                              🕐 {formatDateTime(bill.created_at)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenWorkStatusDialog(bill)}
                        title="Update Work Status"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="success"
                        onClick={() => sendHiViaWhatsApp(bill)}
                        disabled={!bill.customer_phone}
                        title="Send Hi via WhatsApp"
                      >
                        <WhatsAppIcon />
                      </IconButton>
                      <IconButton
                        color="success"
                        onClick={() => shareViaWhatsApp(bill)}
                        disabled={!bill.customer_phone}
                        title="Share via WhatsApp"
                      >
                        <ShareIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}
                  >
                    <Chip
                      label={`Amount: ${formatCurrency(bill.amount || 0)}`}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={`Status: ${(bill.payment_status || "pending").toUpperCase()}`}
                      color={
                        bill.payment_status === "paid"
                          ? "success"
                          : bill.payment_status === "due"
                            ? "error"
                            : "warning"
                      }
                      size="small"
                    />
                    {bill.payment_method && (
                      <Chip
                        label={`Payment: ${bill.payment_method.toUpperCase()}`}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {bill.work_status && (
                      <Chip
                        label={`Work: ${getWorkStatusLabel(bill.work_status)}`}
                        color={getWorkStatusColor(bill.work_status)}
                        size="small"
                        icon={React.createElement(
                          WORK_STATUS_OPTIONS.find(
                            (opt) => opt.value === bill.work_status,
                          )?.icon || CheckCircleIcon,
                        )}
                      />
                    )}
                    {bill.due_amount > 0 && (
                      <Chip
                        label={`Due: ${formatCurrency(bill.due_amount)}`}
                        color="error"
                        size="small"
                      />
                    )}
                    {bill.paid_amount > 0 && (
                      <Chip
                        label={`Paid: ${formatCurrency(bill.paid_amount)}`}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    {/* Services Section */}
                    {bill.items && (
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mb: 1 }}
                        >
                          Services:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {(() => {
                            const services =
                              typeof bill.items === "string"
                                ? bill.items
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter((s) => s)
                                : Array.isArray(bill.items)
                                  ? bill.items
                                  : [];

                            return services.slice(0, 3).map((item, idx) => {
                              const serviceName =
                                typeof item === "string"
                                  ? item
                                  : item.service_name || item.name || "Service";
                              const qty =
                                typeof item === "object"
                                  ? item.quantity || 1
                                  : 1;
                              return (
                                <Chip
                                  key={idx}
                                  label={`${serviceName}${qty > 1 ? ` (${qty}x)` : ""}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "orange.50",
                                    color: "orange.900",
                                  }}
                                />
                              );
                            });
                          })()}
                          {(() => {
                            const services =
                              typeof bill.items === "string"
                                ? bill.items
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter((s) => s)
                                : Array.isArray(bill.items)
                                  ? bill.items
                                  : [];
                            return (
                              services.length > 3 && (
                                <Chip
                                  label={`+${services.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                />
                              )
                            );
                          })()}
                        </Box>
                      </Box>
                    )}

                    {/* Payment Details Section */}
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mb: 1 }}
                      >
                        Payment Details:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {bill.cash_amount > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            💵 Cash: {formatCurrency(bill.cash_amount)}
                          </Typography>
                        )}
                        {bill.upi_amount > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            📱 UPI: {formatCurrency(bill.upi_amount)}
                          </Typography>
                        )}
                        {bill.profit_amount !== undefined &&
                          bill.profit_amount !== null && (
                            <Typography variant="body2" color="text.secondary">
                              💰 Profit: {formatCurrency(bill.profit_amount)}
                            </Typography>
                          )}
                        {bill.remaining_amount > 0 && (
                          <Typography variant="body2" color="error">
                            ⚠️ Remaining:{" "}
                            {formatCurrency(bill.remaining_amount)}
                          </Typography>
                        )}
                        {bill.bill_date && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            📅 Bill Date: {formatDateTime(bill.bill_date)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Work Status Update Dialog */}
      <Dialog
        open={workStatusDialogOpen}
        onClose={handleCloseWorkStatusDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" component="div">
              Work Status & Pending Works
            </Typography>
            {selectedBill && (
              <Typography variant="body2" color="text.secondary">
                Bill: {selectedBill.bill_number || selectedBill.id}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All bills default to <strong>Completed</strong>. Update the status
              when something is pending or still processing.
            </Typography>
            <Chip
              icon={<CheckCircleIcon />}
              label="Default: Completed"
              color="success"
              size="small"
            />
          </Box>

          <RadioGroup
            value={workStatus}
            onChange={(e) => handleWorkStatusChange(e.target.value)}
            sx={{ mb: 3 }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {WORK_STATUS_OPTIONS.map((option) => {
                const IconComponent = option.icon;
                return (
                  <Card
                    key={option.value}
                    sx={{
                      border: workStatus === option.value ? 2 : 1,
                      borderColor:
                        workStatus === option.value
                          ? `${option.color}.main`
                          : "divider",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: `${option.color}.main`,
                        bgcolor: `${option.color}.50`,
                      },
                    }}
                    onClick={() => handleWorkStatusChange(option.value)}
                  >
                    <CardContent>
                      <FormControlLabel
                        value={option.value}
                        control={<Radio />}
                        label=""
                        sx={{ m: 0, width: "100%" }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: 32,
                            color: `${option.color}.main`,
                          }}
                        />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {option.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </RadioGroup>

          <Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Work Notes / Follow-ups
            </Typography>
            <TextareaAutosize
              minRows={3}
              value={workRemark}
              onChange={(e) => setWorkRemark(e.target.value)}
              placeholder="Add notes about the work status..."
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "14px",
                fontFamily: "inherit",
                border: "1px solid #ccc",
                borderRadius: "4px",
                resize: "vertical",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseWorkStatusDialog}
            disabled={updatingWorkStatus}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateWorkStatus}
            variant="contained"
            disabled={updatingWorkStatus}
            startIcon={
              updatingWorkStatus ? (
                <CircularProgress size={16} />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {updatingWorkStatus ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OldSiteBills;
